import { IllustrationOverlay } from '@/components/IllustrationOverlay';
import { ModalsContainer } from '@/components/ModalsContainer';
import { StepContent } from '@/components/StepContent';
import { StepSidebar } from '@/components/StepSidebar';
import Header from '@/components/header';
import { useCameraWorkflow } from '@/hooks/useCameraWorkflow';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { usePendingDraft } from '@/hooks/usePendingDraft';
import { useStepNavigation } from '@/hooks/useStepNavigation';
import {
  firstIncompleteStepAtom,
  localeAtom,
  markedCompleteStepIdsAtom,
  permissionStatusAtom,
  photosByStepIdAtom,
  sessionAtom,
  showPermissionsOverlayAtom,
  stepByIdAtom,
  stepIndexAtom,
} from '@/lib/atoms';
import { GEOLOCATION_CONFIG } from '@/config/constants';
import { getErrorDetails } from '@/lib/errors';
import { isIOSDevice, requestPermissions } from '@/lib/permissions';
import { getLocalizedStepField } from '@/lib/utils';
import { canAddMorePhotos, isStepRequiredDone } from '@transpordiamet/shared';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

const StepPage = () => {
  const { sessionId, stepId } = useParams<{ sessionId: string; stepId: string }>();
  const location = useLocation();
  const session = useAtomValue(sessionAtom);
  const setSession = useSetAtom(sessionAtom);
  const setShowPermissionsOverlay = useSetAtom(showPermissionsOverlayAtom);
  const setPermissionStatus = useSetAtom(permissionStatusAtom);
  const locale = useAtomValue(localeAtom);

  // Refs for auto-scroll functionality
  const activeStepRef = useRef<HTMLDivElement>(null);
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  // Online status
  const isOnline = useOnlineStatus();

  // Checkmarks are visual. Optional steps stay numbered until they have a photo.
  const markedCompleteStepIds = useAtomValue(markedCompleteStepIdsAtom);
  const firstIncompleteStep = useAtomValue(firstIncompleteStepAtom);
  const currentStep = useAtomValue(useMemo(() => stepByIdAtom(stepId), [stepId]));
  const currentStepIndex = useAtomValue(useMemo(() => stepIndexAtom(stepId), [stepId]));
  const photosForCurrentStep = useAtomValue(useMemo(() => photosByStepIdAtom(stepId), [stepId]));

  // Navigation hook
  const { navigateToNextStep, navigateToStep } = useStepNavigation({
    sessionId,
    stepId,
  });
  const [continueError, setContinueError] = useState<string | null>(null);
  const [photoSavedHere, setPhotoSavedHere] = useState(false);
  const arrivedSaved = Boolean((location.state as { photoSaved?: boolean } | null)?.photoSaved);

  useEffect(() => {
    setPhotoSavedHere(false);
  }, [stepId]);

  // The camera hook is created below. The upload calls this once the JPEG is in R2.
  const releaseBytesRef = useRef<() => void>(() => {});

  // Photo upload hook. Declared before the camera so the camera can hand it each photo;
  // its callbacks only run later, once `camera` exists.
  const upload = usePhotoUpload({
    sessionId: sessionId!,
    stepId: stepId!,
    currentStep: currentStep!,
    onStored: () => releaseBytesRef.current(),
    onSuccess: (updatedSession) => {
      setSession(updatedSession);
      setPhotoSavedHere(true);
      camera.clearCapturedPhoto();
      navigateToNextStep(updatedSession, { photoSaved: true }).catch((error) =>
        setContinueError(getErrorDetails(error).message)
      );
    },
    onCheckRejected: () => {
      camera.clearCapturedPhoto();
      camera.handleRetake();
    },
  });

  // Camera workflow hook. A native photo starts uploading as soon as it is ready,
  // an in-page one once the user confirms it on the preview.
  const camera = useCameraWorkflow({
    currentStep: currentStep ?? null,
    sessionId,
    stepId,
    onPhotoReady: upload.startUpload,
  });
  releaseBytesRef.current = camera.releaseCapturedBytes;

  const handleContinue = useCallback(async () => {
    if (!session) return;

    try {
      setContinueError(null);
      await navigateToNextStep(session, { force: true });
    } catch (error) {
      setContinueError(getErrorDetails(error).message);
    }
  }, [session, navigateToNextStep]);

  usePendingDraft(
    stepId,
    session?.pendingDrafts,
    !camera.capturedPhoto && !upload.showCheckConfirmation,
    upload.presentDraft
  );

  // Redirect to first incomplete required step if user lands ahead of unfinished work.
  // Do NOT kick off a required-done step that still allows optional extras.
  useEffect(() => {
    if (!session || !sessionId || !stepId || !currentStep) return;

    const count = photosForCurrentStep.length;
    if (isStepRequiredDone(currentStep, count) && canAddMorePhotos(currentStep, count)) {
      return;
    }

    if (firstIncompleteStep && firstIncompleteStep.id !== stepId) {
      navigateToStep(firstIncompleteStep.id);
    }
  }, [session, sessionId, stepId, currentStep, photosForCurrentStep.length, firstIncompleteStep, navigateToStep]);

  // Auto-scroll to active step only on initial mount
  useEffect(() => {
    // Scroll to next step within steps container only (not the entire page)
    if (activeStepRef.current && stepsContainerRef.current) {
      const container = stepsContainerRef.current;
      const activeStep = activeStepRef.current;

      // Get positions relative to container
      const containerRect = container.getBoundingClientRect();
      const stepRect = activeStep.getBoundingClientRect();

      // Calculate scroll offset to center the step in the container
      const scrollOffset = stepRect.top - containerRect.top - containerRect.height / 2 + stepRect.height / 2;

      // Scroll only the steps container
      container.scrollBy({ top: scrollOffset, behavior: 'smooth' });
    }
  }, [stepId]);

  // Permission guard. iOS Safari often denies getCurrentPosition without a tap,
  // so skip the auto-probe and wait for the overlay button (user gesture).
  // The camera alone needs no tap.
  useEffect(() => {
    if (GEOLOCATION_CONFIG.ENABLED && isIOSDevice()) {
      setShowPermissionsOverlay(true);
      return;
    }

    const checkPermissions = async () => {
      const status = await requestPermissions();
      if (!status.granted) {
        setPermissionStatus(status);
        setShowPermissionsOverlay(true);
      }
    };

    checkPermissions();
  }, [setPermissionStatus, setShowPermissionsOverlay]);

  // Combined error from camera and upload workflows
  const displayError = camera.error || upload.error;

  // Back into the native camera. The error stays up until the new photo starts
  // uploading, so a cancelled camera lands back on it.
  const handleCapturePhoto = () => {
    camera.handleRetake();
  };

  const handlePhotoBack = () => {
    upload.clearError();
    camera.clearCapturedPhoto();
  };

  if (!session || !currentStep) {
    return (
      <div className="min-h-screen bg-background p-8">
        <Header />
        <div className="mt-8">
          <p>Loading step information...</p>
        </div>
      </div>
    );
  }

  // Get localized step title and description for illustration overlay
  const stepTitle = getLocalizedStepField(currentStep, 'title', locale);
  const stepDescription = getLocalizedStepField(currentStep, 'description', locale);

  return (
    <div className="landscape-only bg-background">
      <div className="relative h-full min-h-0 landscape:h-dvh">
        {camera.showIllustration && currentStep.illustration_url ? (
          <IllustrationOverlay
            illustrationUrl={currentStep.illustration_url}
            stepTitle={stepTitle}
            stepDescription={stepDescription}
            onOk={camera.handleIllustrationOk}
            onBack={camera.handleIllustrationBack}
          />
        ) : (
          <div className="grid grid-cols-[minmax(220px,28%)_minmax(0,1fr)] h-full min-h-0">
            <StepSidebar
              steps={session.configuration.steps}
              currentStepId={stepId}
              completedStepIds={markedCompleteStepIds}
              locale={locale}
              activeStepRef={activeStepRef}
              stepsContainerRef={stepsContainerRef}
            />
            <StepContent
              currentStep={currentStep}
              photosCount={photosForCurrentStep.length}
              currentStepIndex={currentStepIndex}
              totalSteps={session.configuration.steps.length}
              isOnline={isOnline}
              locale={locale}
              onCameraClick={camera.handleCameraClick}
              onContinue={handleContinue}
              continueError={continueError}
              photoSaved={photoSavedHere || arrivedSaved}
            />
          </div>
        )}

        <ModalsContainer
          showViewfinder={camera.showViewfinder}
          onViewfinderShot={camera.acceptInPagePhoto}
          onViewfinderError={camera.handleViewfinderError}
          onViewfinderBack={camera.closeViewfinder}
          cameraInputRef={camera.cameraInputRef}
          onCameraInputChange={camera.handleCameraInputChange}
          processingPhoto={camera.processingPhoto}
          waitingForLocation={
            camera.gpsPosition || !GEOLOCATION_CONFIG.ENABLED ? false : camera.gpsFallback ? 'network' : 'gps'
          }
          cameraError={camera.error}
          onCameraErrorClose={camera.photoLost ? camera.recoverLostPhoto : camera.dismissError}
          cameraErrorRecovers={camera.photoLost}
          // In-page photo preview
          awaitingApproval={camera.awaitingApproval}
          onPhotoConfirm={camera.confirmPhoto}
          onPhotoRetake={camera.handleRetake}
          // Upload progress
          capturedPhoto={camera.capturedPhoto}
          uploadStage={upload.stage}
          r2PhotoUrl={upload.r2PhotoUrl}
          error={displayError}
          onCapturePhoto={handleCapturePhoto}
          onPhotoBack={handlePhotoBack}
          // Check Confirmation
          showCheckConfirmation={upload.showCheckConfirmation}
          pendingCheckData={upload.pendingCheckData}
          confirming={upload.confirming}
          expectedVin={session?.vinCode}
          onCheckConfirm={upload.handleConfirmCheck}
          onCheckRetake={upload.handleRejectCheck}
        />
      </div>
    </div>
  );
};

export default StepPage;
